/**
 * VLAN Detail View page.
 * Visualizes a single VLAN and its connected interfaces.
 * Implements FR-3.3.1: VLAN Membership View
 */

import { FC, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TableSortLabel,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { GraphVisualization, GraphControls } from '@/components/Graph';
import api from '@/services/api';
import type {
  VlanQueryResponse,
  VlanSummary,
  GraphData,
  GraphNode,
  GraphEdge,
  Interface,
  RelationshipInfo,
  VlanConfigurationSummary,
} from '@/types/entities';
import {
  Warning as WarningIcon,
} from '@mui/icons-material';

type SortField = 'name' | 'mode' | 'status';
type SortOrder = 'asc' | 'desc';

export const VlanDetailView: FC = () => {
  const graphContainerRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedVlan, setSelectedVlan] = useState<VlanSummary | null>(null);
  const [vlans, setVlans] = useState<VlanSummary[]>([]);
  const [vlansLoading, setVlansLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [vlanData, setVlanData] = useState<VlanQueryResponse | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const abortControllerRef = useRef<AbortController | null>(null);
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);

  /**
   * Load all VLANs on component mount
   */
  useEffect(() => {
    const loadVlans = async () => {
      setVlansLoading(true);
      try {
        const response = await api.getVlans();
        setVlans(response.vlans);
      } catch (err) {
        console.error('Failed to load VLANs:', err);
        // Don't show error for VLAN list loading - user can still type VLAN ID
      } finally {
        setVlansLoading(false);
      }
    };

    loadVlans();
  }, []);

  /**
   * Auto-focus and open autocomplete when navigating from dashboard
   */
  useEffect(() => {
    if (searchParams.get('focus') === 'true' && vlans.length > 0) {
      // Use setTimeout to ensure component is fully mounted and DOM is ready
      setTimeout(() => {
        // Focus the input
        autocompleteInputRef.current?.focus();
        // Open the dropdown
        setAutocompleteOpen(true);
      }, 100);

      // Clean up URL parameter to prevent re-triggering on re-renders
      const params = new URLSearchParams(window.location.search);
      params.delete('focus');
      window.history.replaceState({}, '', `${window.location.pathname}`);
    }
  }, [searchParams, vlans]);

  /**
   * Auto-load VLAN from URL query parameter
   */
  useEffect(() => {
    const vlanIdParam = searchParams.get('id');
    if (vlanIdParam && vlans.length > 0 && !loading) {
      const vlanIdNum = parseInt(vlanIdParam, 10);
      if (!isNaN(vlanIdNum) && vlanIdNum >= 1 && vlanIdNum <= 4094) {
        // Only auto-load if we haven't already loaded this VLAN
        if (!vlanData || vlanData.vlan.vlanID !== vlanIdNum) {
          const vlanToLoad = vlans.find((v) => v.vlanID === vlanIdNum) || { vlanID: vlanIdNum };
          // Simulate autocomplete selection by calling handleVlanChange
          handleVlanChange({} as React.SyntheticEvent, vlanToLoad);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, vlans, vlanData, loading]);

  /**
   * Transform VlanQueryResponse to GraphData format
   */
  const transformToGraphData = useCallback(
    (data: VlanQueryResponse): GraphData => {
      const nodes: GraphNode[] = [];
      const edges: GraphEdge[] = [];

      // Phase 3: Build VLAN label with name variants if inconsistent
      let vlanLabel = `VLAN ${data.vlan.vlanID}`;
      if (data.vlan_configs && data.vlan_configs.length > 1) {
        const nameSet = new Set<string>();
        data.vlan_configs.forEach((config) => {
          if (config.name) {
            nameSet.add(config.name);
          }
        });

        if (nameSet.size > 1) {
          // Multiple names - show them all
          vlanLabel += `\n${Array.from(nameSet).join(' / ')}`;
        } else if (nameSet.size === 1) {
          // Single consistent name
          vlanLabel += `\n${Array.from(nameSet)[0]}`;
        }
      } else if (data.vlan.name) {
        // Fallback to canonical VLAN name
        vlanLabel += `\n${data.vlan.name}`;
      }

      // Add VLAN node (top of hierarchy)
      nodes.push({
        id: `vlan-${data.vlan.vlanID}`,
        label: vlanLabel,
        type: 'vlan',
        data: data.vlan,
      });

      // Add Interface nodes
      data.interfaces.forEach((iface) => {
        nodes.push({
          id: iface.uniqueId,
          label: iface.name,
          type: 'interface',
          data: iface,
        });
      });

      // Add Switch nodes
      if (data.switches) {
        data.switches.forEach((sw) => {
          nodes.push({
            id: sw.hostname,
            label: sw.hostname,
            type: 'switch',
            data: sw,
          });
        });
      }

      // Add edges: VLAN → Interface (based on relationships)
      data.relationships.forEach((rel) => {
        const iface = data.interfaces.find((i) => i.uniqueId === rel.interfaceId);
        if (!iface) return;

        edges.push({
          id: `vlan-${data.vlan.vlanID}-${rel.interfaceId}`,
          source: `vlan-${data.vlan.vlanID}`,
          target: rel.interfaceId,
          type: rel.type,
          label:
            rel.type === 'TRUNKS_VLAN' && rel.properties.native ? 'native' : rel.type === 'TRUNKS_VLAN' ? 'trunk' : 'member',
          data: {
            native: rel.properties.native,
          },
        });
      });

      // Add edges: Interface → Switch (extract switch from uniqueId)
      data.interfaces.forEach((iface) => {
        // uniqueId format: "{switchHostname}-{interfaceName}"
        // Example: "SW-TEST-01-Gi1/0/1"
        const switchHostname = iface.uniqueId.split('-').slice(0, -1).join('-');

        // Only add edge if switch exists in data
        const switchExists = data.switches?.some((sw) => sw.hostname === switchHostname);
        if (switchExists) {
          edges.push({
            id: `${iface.uniqueId}-${switchHostname}`,
            source: iface.uniqueId,
            target: switchHostname,
            type: 'HAS_INTERFACE',
            label: '',
          });
        }
      });

      return { nodes, edges };
    },
    []
  );

  /**
   * Extract switch hostname from interface uniqueId
   * uniqueId format: "{switchHostname}-{interfaceName}"
   * Example: "SW-TEST-01-Gi1/0/1" -> "SW-TEST-01"
   */
  const extractSwitchHostname = useCallback((uniqueId: string): string => {
    // Split by '-' and take all parts except the last one (interface name)
    const parts = uniqueId.split('-');
    return parts.slice(0, -1).join('-');
  }, []);

  /**
   * Get unique list of switches from interfaces
   */
  const getUniqueSwitches = useCallback((data: VlanQueryResponse): string[] => {
    const switchSet = new Set<string>();
    data.interfaces.forEach((iface) => {
      const switchHostname = extractSwitchHostname(iface.uniqueId);
      if (switchHostname) {
        switchSet.add(switchHostname);
      }
    });
    return Array.from(switchSet);
  }, [extractSwitchHostname]);

  /**
   * Handle VLAN selection from autocomplete
   * Implements hybrid approach: updates URL and fetches data immediately
   */
  const handleVlanChange = useCallback(
    async (_event: React.SyntheticEvent, value: string | VlanSummary | null) => {
      // If value is a string (typed but not selected), parse as VLAN ID
      let vlanToFetch: VlanSummary | null = null;

      if (typeof value === 'string') {
        const vlanIdNum = parseInt(value, 10);
        if (!isNaN(vlanIdNum) && vlanIdNum >= 1 && vlanIdNum <= 4094) {
          // Find VLAN by ID if user typed it
          vlanToFetch = vlans.find((v) => v.vlanID === vlanIdNum) || { vlanID: vlanIdNum };
        }
      } else {
        vlanToFetch = value;
      }

      setSelectedVlan(vlanToFetch);

      if (!vlanToFetch) {
        setVlanData(null);
        setError(null);
        // Clear URL param
        setSearchParams({}, { replace: true });
        return;
      }

      // Update URL immediately (replace history to avoid back button clutter)
      setSearchParams({ id: String(vlanToFetch.vlanID) }, { replace: true });

      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Fetch VLAN data
      setLoading(true);
      setError(null);
      setVlanData(null);

      try {
        const data = await api.getVlan(vlanToFetch.vlanID);

        // Only update state if this request wasn't aborted
        if (!controller.signal.aborted) {
          setVlanData(data);
          setError(null);
          // Scroll to top for better UX when switching VLANs
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (err) {
        // Don't show error if request was aborted (user changed selection)
        if (!controller.signal.aborted) {
          if (err instanceof Error) {
            setError(`Failed to fetch VLAN ${vlanToFetch.vlanID}: ${err.message}`);
          } else {
            setError(
              `Failed to fetch VLAN ${vlanToFetch.vlanID}. Please check if the backend is running.`
            );
          }
          setVlanData(null);
        }
      } finally {
        // Only clear loading if this request wasn't aborted
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [vlans, setSearchParams]
  );

  /**
   * Export data as CSV
   */
  const handleExportCSV = useCallback(() => {
    if (!vlanData) return;

    // Build CSV content
    const headers = [
      'Interface Name',
      'Unique ID',
      'Switch',
      'Mode',
      'Status',
      'Description',
      'Relationship Type',
      'Native VLAN',
    ];
    const rows = vlanData.interfaces.map((iface) => {
      const rel = vlanData.relationships.find((r) => r.interfaceId === iface.uniqueId);
      return [
        iface.name,
        iface.uniqueId,
        extractSwitchHostname(iface.uniqueId),
        iface.mode || 'unknown',
        iface.status || 'unknown',
        iface.description || '',
        rel?.type || '',
        rel?.properties.native ? 'Yes' : 'No',
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vlan-${vlanData.vlan.vlanID}-interfaces.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [vlanData, extractSwitchHostname]);

  /**
   * Export data as JSON
   */
  const handleExportJSON = useCallback(() => {
    if (!vlanData) return;

    const jsonContent = JSON.stringify(vlanData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vlan-${vlanData.vlan.vlanID}-data.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [vlanData]);

  /**
   * Get relationship info for an interface
   */
  const getRelationshipInfo = useCallback(
    (uniqueId: string): RelationshipInfo | undefined => {
      return vlanData?.relationships.find((r) => r.interfaceId === uniqueId);
    },
    [vlanData]
  );

  /**
   * Memoized graph data to prevent unnecessary re-renders
   */
  const graphData = useMemo(() => {
    if (!vlanData) return { nodes: [], edges: [] };
    return transformToGraphData(vlanData);
  }, [vlanData, transformToGraphData]);

  /**
   * Handle table sort
   */
  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortOrder('asc');
      }
    },
    [sortField, sortOrder]
  );

  /**
   * Sort interfaces
   */
  const sortedInterfaces = useCallback(
    (interfaces: Interface[]): Interface[] => {
      return [...interfaces].sort((a, b) => {
        let aValue: string | number = '';
        let bValue: string | number = '';

        switch (sortField) {
          case 'name':
            aValue = a.name || '';
            bValue = b.name || '';
            break;
          case 'mode':
            aValue = a.mode || '';
            bValue = b.mode || '';
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    },
    [sortField, sortOrder]
  );

  /**
   * Process VLAN configurations to create per-switch summaries
   * Phase 1: Configuration table data
   */
  const vlanConfigSummaries = useMemo((): VlanConfigurationSummary[] => {
    if (!vlanData?.vlan_configs) return [];

    // Group interfaces by switch
    const interfacesBySwitch = new Map<string, number>();
    vlanData.interfaces.forEach((iface) => {
      const switchHostname = extractSwitchHostname(iface.uniqueId);
      interfacesBySwitch.set(switchHostname, (interfacesBySwitch.get(switchHostname) || 0) + 1);
    });

    // Create summaries from vlan_configs
    return vlanData.vlan_configs.map((config) => ({
      switchHostname: config.switchHostname,
      vlanName: config.name || '(unnamed)',
      status: config.status || 'unknown',
      interfaceCount: interfacesBySwitch.get(config.switchHostname) || 0,
    }));
  }, [vlanData, extractSwitchHostname]);

  /**
   * Detect VLAN name inconsistencies across switches
   * Phase 2: Alert banner logic
   */
  const vlanNameInconsistency = useMemo(() => {
    if (!vlanData?.vlan_configs || vlanData.vlan_configs.length <= 1) {
      return { hasInconsistency: false, uniqueNames: [], canonicalName: '' };
    }

    // Get all unique VLAN names (excluding undefined/null)
    const nameSet = new Set<string>();
    vlanData.vlan_configs.forEach((config) => {
      if (config.name) {
        nameSet.add(config.name);
      }
    });

    const uniqueNames = Array.from(nameSet);
    const hasInconsistency = uniqueNames.length > 1;

    // Determine canonical name (most common, or first if tie)
    let canonicalName = '';
    if (uniqueNames.length > 0) {
      const nameCounts = new Map<string, number>();
      vlanData.vlan_configs.forEach((config) => {
        if (config.name) {
          nameCounts.set(config.name, (nameCounts.get(config.name) || 0) + 1);
        }
      });

      // Find most common name
      let maxCount = 0;
      nameCounts.forEach((count, name) => {
        if (count > maxCount) {
          maxCount = count;
          canonicalName = name;
        }
      });
    }

    return { hasInconsistency, uniqueNames, canonicalName };
  }, [vlanData]);

  /**
   * Cleanup: abort any in-flight requests on unmount
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        VLAN Detail View
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Search for a VLAN to view its properties, connected interfaces, and graph visualization.
      </Typography>

      {/* Search Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Autocomplete
            options={vlans}
            value={selectedVlan}
            onChange={handleVlanChange}
            open={autocompleteOpen}
            onOpen={() => setAutocompleteOpen(true)}
            onClose={() => setAutocompleteOpen(false)}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              const name = option.name ? ` - ${option.name}` : '';
              const status = option.status ? ` (${option.status})` : '';
              return `${option.vlanID}${name}${status}`;
            }}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={key} {...otherProps}>
                  <Box>
                    <Typography variant="body1">
                      VLAN {option.vlanID}
                      {option.name && ` - ${option.name}`}
                    </Typography>
                    {option.status && (
                      <Typography variant="caption" color="text.secondary">
                        Status: {option.status}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={autocompleteInputRef}
                label="Select VLAN"
                placeholder="Type VLAN ID or name to search..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {vlansLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            loading={vlansLoading}
            disabled={loading}
            freeSolo
            sx={{ flexGrow: 1 }}
          />
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* VLAN Properties */}
      {vlanData && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">VLAN Properties</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Export as CSV">
                    <IconButton size="small" onClick={handleExportCSV}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export as JSON">
                    <IconButton size="small" onClick={handleExportJSON}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    VLAN ID
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {vlanData.vlan.vlanID}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">{vlanData.vlan.name || 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={vlanData.vlan.status || 'unknown'}
                    color={vlanData.vlan.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Interfaces
                  </Typography>
                  <Typography variant="body1">{vlanData.interfaces.length}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Switches
                  </Typography>
                  <Typography variant="body1">
                    {getUniqueSwitches(vlanData).length}
                    {getUniqueSwitches(vlanData).length > 0 && (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' '}({getUniqueSwitches(vlanData).join(', ')})
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Phase 2: Name Inconsistency Alert */}
          {vlanNameInconsistency.hasInconsistency && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                VLAN Name Inconsistency Detected
              </Typography>
              <Typography variant="body2">
                This VLAN has different names configured across switches:{' '}
                <strong>{vlanNameInconsistency.uniqueNames.join(', ')}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Most common name: <strong>{vlanNameInconsistency.canonicalName}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                See the configuration table below for per-switch details.
              </Typography>
            </Alert>
          )}

          {/* Phase 1: Configuration Table */}
          {vlanConfigSummaries.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Per-Switch Configuration ({vlanConfigSummaries.length} switches)
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Shows how this VLAN is configured on each switch. Name differences indicate inconsistent configuration.
                </Typography>
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Switch Hostname</TableCell>
                        <TableCell>VLAN Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Interface Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vlanConfigSummaries.map((summary) => {
                        const isInconsistentName =
                          vlanNameInconsistency.hasInconsistency &&
                          summary.vlanName !== vlanNameInconsistency.canonicalName &&
                          summary.vlanName !== '(unnamed)';

                        return (
                          <TableRow
                            key={summary.switchHostname}
                            sx={{
                              backgroundColor: isInconsistentName ? 'rgba(255, 152, 0, 0.08)' : 'inherit',
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {summary.switchHostname}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">{summary.vlanName}</Typography>
                                {isInconsistentName && (
                                  <Tooltip title="This name differs from the most common name">
                                    <WarningIcon fontSize="small" color="warning" />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={summary.status}
                                size="small"
                                color={summary.status === 'active' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">{summary.interfaceCount}</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Graph Visualization */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Graph Visualization
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Interactive graph showing VLAN and connected interfaces. Green = access port, Blue = trunk port.
                {vlanNameInconsistency.hasInconsistency && ' VLAN node shows all name variants when configured differently across switches.'}
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <GraphVisualization
                  ref={graphContainerRef}
                  data={graphData}
                  height="500px"
                  layout="breadthfirst"
                  centerNodeId={`vlan-${vlanData.vlan.vlanID}`}
                />
                <GraphControls containerRef={graphContainerRef} />
              </Box>
            </CardContent>
          </Card>

          {/* Interface List Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Interface List ({vlanData.interfaces.length} interfaces)
              </Typography>
              {vlanData.interfaces.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No interfaces connected to this VLAN.
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            active={sortField === 'name'}
                            direction={sortField === 'name' ? sortOrder : 'asc'}
                            onClick={() => handleSort('name')}
                          >
                            Interface Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Switch</TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortField === 'mode'}
                            direction={sortField === 'mode' ? sortOrder : 'asc'}
                            onClick={() => handleSort('mode')}
                          >
                            Mode
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={sortField === 'status'}
                            direction={sortField === 'status' ? sortOrder : 'asc'}
                            onClick={() => handleSort('status')}
                          >
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Relationship</TableCell>
                        <TableCell align="center">Native VLAN</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedInterfaces(vlanData.interfaces).map((iface) => {
                        const rel = getRelationshipInfo(iface.uniqueId);
                        const isAccess = iface.mode === 'access' || rel?.type === 'MEMBER_OF';
                        const isTrunk = iface.mode === 'trunk' || rel?.type === 'TRUNKS_VLAN';
                        const isNative = rel?.properties.native === true;

                        return (
                          <TableRow
                            key={iface.uniqueId}
                            sx={{
                              backgroundColor: isAccess
                                ? 'rgba(46, 125, 50, 0.08)'
                                : isTrunk
                                ? 'rgba(2, 136, 209, 0.08)'
                                : 'inherit',
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {iface.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {iface.uniqueId}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {extractSwitchHostname(iface.uniqueId)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={iface.mode || 'unknown'}
                                size="small"
                                color={isAccess ? 'success' : isTrunk ? 'info' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {iface.status === 'up' ? (
                                  <CheckCircleIcon fontSize="small" color="success" />
                                ) : (
                                  <CancelIcon fontSize="small" color="error" />
                                )}
                                <Typography variant="body2">{iface.status || 'unknown'}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {iface.description || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={rel?.type || 'N/A'}
                                size="small"
                                variant="outlined"
                                color={rel?.type === 'MEMBER_OF' ? 'success' : 'info'}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {isTrunk && isNative ? (
                                <Chip label="Native" size="small" color="warning" />
                              ) : isTrunk ? (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};
